'use client';

import { useEffect, useRef, useState } from 'react';
import { useIdentityToken, usePrivy } from '@privy-io/react-auth';
import { syncMe } from '@/lib/auth';

/**
 * Navbar auth control, slotted into voice-panel's header. Four states:
 *
 *   - Privy not configured (`NEXT_PUBLIC_PRIVY_APP_ID` unset)
 *     → renders nothing. Anonymous mode unchanged.
 *   - Privy booting (`ready === false`)
 *     → reserved-space placeholder so the navbar doesn't shift.
 *   - Not authenticated
 *     → `SIGN IN` button. Click opens the Privy modal.
 *   - Authenticated
 *     → user pill (truncated email / wallet address) with a `↳` sign-out
 *       affordance on hover.
 *
 * On the auth → authenticated transition, calls `POST /api/users/me`
 * once with the Privy **identity token** (not the access token) to seed
 * our internal user row. The backend's `getUser({ idToken })` parses
 * linked accounts directly out of the ID token's payload; the shorter
 * access token has a different shape and trips JSON.parse inside the
 * SDK. Everywhere else (conversation endpoints) still uses the access
 * token, which the PrivyAuthGuard verifies via `verifyAuthToken`.
 *
 * Failure is non-blocking (shows as a small dot indicator on the chip)
 * so the user isn't stuck in a half-authenticated state. The sync runs
 * once per session; subsequent backend calls just verify the JWT.
 */
export function AuthButton() {
  const appId = process.env.NEXT_PUBLIC_PRIVY_APP_ID;
  if (!appId) return null;
  return <AuthButtonInner />;
}

function AuthButtonInner() {
  const { ready, authenticated, user, login, logout, getAccessToken } = usePrivy();
  // Privy can issue two tokens for an authenticated user:
  //   - identityToken: JWT with `linked_accounts` baked into the payload.
  //     The backend's `client.getUser({ idToken })` decodes it directly,
  //     no Privy API round-trip. Preferred path.
  //   - access token: shorter JWT for API auth. The backend has a
  //     fallback that catches the access-token shape and recovers via
  //     `getUserById(did)` — slower, one extra Privy round-trip, but
  //     always works.
  //
  // We send whichever is available. Some Privy app configurations don't
  // expose the identity token to the React SDK (it remains null
  // indefinitely), so falling back to the access token is necessary —
  // the backend handles either shape.
  const { identityToken } = useIdentityToken();
  // Ref-based exactly-once guarantee. State reads in effects reflect the
  // value at effect-creation time, so a `syncing` state guard can read
  // stale `false` if React re-renders mid-sync. The ref is mutated
  // synchronously and never stale across the effect's lifetime.
  const syncStateRef = useRef<'idle' | 'syncing' | 'done'>('idle');
  const [meDisplayName, setMeDisplayName] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  useEffect(() => {
    // Auth flipped off → reset all per-session state so a fresh sign-in
    // re-runs the sync. Without this, signing out and back in as a
    // different user wouldn't pick up the new displayName.
    if (!authenticated) {
      syncStateRef.current = 'idle';
      setMeDisplayName(null);
      setSyncError(null);
      return;
    }
    if (syncStateRef.current !== 'idle') return;
    syncStateRef.current = 'syncing';

    let cancelled = false;
    (async () => {
      try {
        // Prefer identity token; fall back to access token if Privy
        // hasn't surfaced one. Backend handles either.
        const token = identityToken ?? (await getAccessToken());
        if (!token) throw new Error('no Privy token available');
        const me = await syncMe(token);
        if (cancelled) return;
        setMeDisplayName(me.displayName ?? null);
        setSyncError(null);
        syncStateRef.current = 'done';
      } catch (err) {
        if (cancelled) return;
        setSyncError((err as Error).message);
        // Allow a retry on the next render-cycle's effect fire (e.g.
        // if `authenticated` flips off and back on, the cleanup branch
        // above will reset the ref).
        syncStateRef.current = 'idle';
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, identityToken]);

  if (!ready) return <Placeholder />;

  if (!authenticated) {
    return (
      <button
        type="button"
        onClick={() => login()}
        className="font-mono font-bold text-[11px] tracking-widest text-on-surface-variant hover:text-on-surface transition-colors px-3 py-2"
      >
        SIGN IN
      </button>
    );
  }

  return (
    <UserChip
      user={user}
      serverDisplayName={meDisplayName}
      onSignOut={() => void logout()}
      syncError={syncError}
    />
  );
}

function Placeholder() {
  // Reserves the same vertical space as the SIGN IN button / UserChip
  // so the navbar doesn't twitch when Privy finishes booting.
  return <span className="inline-block w-20 h-7" aria-hidden />;
}

interface PrivyUserLike {
  email?: { address?: string } | null;
  wallet?: { address?: string } | null;
}

function UserChip({
  user,
  serverDisplayName,
  onSignOut,
  syncError,
}: {
  user: PrivyUserLike | null | undefined;
  serverDisplayName: string | null;
  onSignOut: () => void;
  syncError: string | null;
}) {
  const [hover, setHover] = useState(false);
  // Prefer the server-side displayName (set during the /api/users/me
  // sync) so the chip stays consistent with anything else on the
  // platform that surfaces the user's name. Fall back to deriving from
  // Privy's user object during the brief pre-sync window.
  const label = serverDisplayName
    ? truncateLabel(serverDisplayName)
    : deriveLabel(user);

  return (
    <div
      className="flex items-center gap-2"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <span
        className="font-mono text-[11px] tracking-widest text-on-surface bg-surface-container-low border border-outline-variant px-3 py-1.5 flex items-center gap-2"
        title={syncError ? `sync warning: ${syncError}` : undefined}
      >
        {label}
        {syncError && (
          <span
            className="w-1.5 h-1.5 rounded-full bg-error"
            aria-label="account sync failed"
          />
        )}
      </span>
      <button
        type="button"
        onClick={onSignOut}
        className={`font-mono text-[10px] tracking-widest text-outline hover:text-on-surface-variant transition-all ${
          hover ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        aria-label="Sign out"
      >
        SIGN OUT
      </button>
    </div>
  );
}

function deriveLabel(user: PrivyUserLike | null | undefined): string {
  if (!user) return '…';
  const email = user.email?.address;
  if (email) return truncateLabel(email.split('@')[0] ?? email);
  const addr = user.wallet?.address;
  if (addr) return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
  return 'SIGNED IN';
}

function truncateLabel(s: string): string {
  return s.length > 18 ? `${s.slice(0, 16)}…` : s;
}
